/** Docs (UA): see modules/game/DOCS.md — pure game engine, no DOM/canvas here */

import { GAME_CONFIG, ItemKind } from '../game.constants';

export interface FallingItem {
    id: number;
    kind: ItemKind;
    /** Normalized 0..1 across the play-field width. */
    x: number;
    /** Normalized 0..1 from spawn line (0) to catch line (1). */
    y: number;
    /** Normalized units per second. */
    vy: number;
    /** Zigzag phase — only the bug flies a sine path. */
    born: number;
    rotation: number;
}

export type GameEvent =
    | { type: 'catch'; kind: ItemKind; x: number; score: number }
    | { type: 'hit'; x: number; livesLeft: number }
    | { type: 'threshold' }
    | { type: 'multiplierStart' }
    | { type: 'multiplierEnd' }
    | { type: 'finished'; reason: 'time' | 'lives' | 'threshold' };

export interface GameState {
    time: number;
    score: number;
    lives: number;
    cupX: number;
    targetX: number;
    items: FallingItem[];
    multUntil: number;
    iframesUntil: number;
    thresholdTakenAt: number | null;
    finished: boolean;
    finishReason: 'time' | 'lives' | 'threshold' | null;
}

interface SpawnClock {
    nextSeedAt: number;
    nextGoldAt: number;
    nextBadAt: number;
    multQueue: number[];
    nextId: number;
}

const C = GAME_CONFIG;

/** mulberry32 — deterministic PRNG so a session can be replayed in tests. */
export const createRng = (seed: number): (() => number) => {
    let a = seed >>> 0;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

interface PhaseValues {
    seedRate: number;
    fallTime: number;
    goldEvery: number;
    badEvery: number;
}

const phaseAt = (time: number): PhaseValues => {
    const phases = C.PHASES;
    let i = 0;
    while (i < phases.length - 1 && phases[i + 1].t <= time) i += 1;
    if (i >= phases.length - 1) {
        const last = phases[phases.length - 1];
        return { seedRate: last.seedRate, fallTime: last.fallTime, goldEvery: last.goldEvery, badEvery: last.badEvery };
    }
    const a = phases[i];
    const b = phases[i + 1];
    const k = (time - a.t) / (b.t - a.t);
    const lerp = (x: number, y: number): number => x + (y - x) * k;
    return {
        seedRate: lerp(a.seedRate, b.seedRate),
        fallTime: lerp(a.fallTime, b.fallTime),
        goldEvery: lerp(a.goldEvery, b.goldEvery),
        badEvery: lerp(a.badEvery, b.badEvery),
    };
};

export interface GameEngine {
    state: GameState;
    /** Advance the world; targetX is the finger/cursor X normalized 0..1. */
    tick: (dt: number, targetX: number) => GameEvent[];
}

export const createGameEngine = (seed: number): GameEngine => {
    const rng = createRng(seed);
    const state: GameState = {
        time: 0,
        score: 0,
        lives: C.LIVES,
        cupX: 0.5,
        targetX: 0.5,
        items: [],
        multUntil: -1,
        iframesUntil: -1,
        thresholdTakenAt: null,
        finished: false,
        finishReason: null,
    };
    const clock: SpawnClock = {
        nextSeedAt: 0.5,
        nextGoldAt: C.FIRST_GOLD_AT,
        nextBadAt: C.FIRST_BAD_AT,
        multQueue: [...C.MULTIPLIER.spawnAt],
        nextId: 1,
    };

    const jitter = (amount: number): number => 1 + (rng() * 2 - 1) * amount;

    const spawn = (kind: ItemKind, fallTime: number): void => {
        const factor =
            kind === 'gold' ? C.GOLD_FALL_FACTOR : kind === 'mult' ? C.MULT_FALL_FACTOR : 1;
        state.items.push({
            id: clock.nextId++,
            kind,
            x: C.SPAWN_MARGIN + rng() * (1 - 2 * C.SPAWN_MARGIN),
            y: 0,
            vy: 1 / (fallTime * factor),
            born: state.time,
            rotation: (rng() * 2 - 1) * 15,
        });
    };

    const tick = (dt: number, targetX: number): GameEvent[] => {
        const events: GameEvent[] = [];
        if (state.finished) return events;

        state.time += dt;
        state.targetX = targetX;
        const phase = phaseAt(state.time);

        if (state.time >= clock.nextSeedAt) {
            spawn('seed', phase.fallTime);
            clock.nextSeedAt = state.time + (1 / phase.seedRate) * jitter(C.SEED_JITTER);
        }
        if (state.time >= clock.nextGoldAt) {
            spawn('gold', phase.fallTime);
            clock.nextGoldAt = state.time + phase.goldEvery * jitter(C.SPAWN_JITTER);
        }
        if (state.time >= clock.nextBadAt && state.time < C.NO_BUGS_AFTER) {
            spawn('bug', phase.fallTime);
            clock.nextBadAt = state.time + phase.badEvery * jitter(C.SPAWN_JITTER);
        }
        if (clock.multQueue.length > 0 && state.time >= clock.multQueue[0]) {
            clock.multQueue.shift();
            spawn('mult', phase.fallTime);
        }

        // Cup chase: exponential smoothing + hard speed cap. No overshoot
        // by construction — see the design doc, block 3.5.
        const ideal = (targetX - state.cupX) * Math.min(C.CUP.smoothing * dt, 1);
        const maxStep = C.CUP.maxSpeed * dt;
        state.cupX += Math.min(Math.max(ideal, -maxStep), maxStep);
        const halfCup = C.CUP.width / 2;
        state.cupX = Math.min(Math.max(state.cupX, halfCup), 1 - halfCup);

        const wasMultActive = state.time - dt < state.multUntil;
        const multActive = state.time < state.multUntil;
        if (wasMultActive && !multActive) events.push({ type: 'multiplierEnd' });

        // Ущільнення на місці: новий масив kept на кожен кадр — зайве сміття
        // в циклі, який крутиться 60 разів на секунду.
        let write = 0;
        for (let read = 0; read < state.items.length; read += 1) {
            const item = state.items[read];
            item.y += item.vy * dt;
            if (item.y < 1) {
                state.items[write] = item;
                write += 1;
                continue;
            }
            // The bug zigzags: its effective X shifts on a sine path.
            const x = item.kind === 'bug' ? bugX(item, state.time) : item.x;
            // Reach follows the sprite: a big golden bean is caught by its edge,
            // not by its centre.
            const catchDist = halfCup + C.ITEM_RADIUS[item.kind];
            if (Math.abs(x - state.cupX) > catchDist) continue; // missed: costs nothing

            if (item.kind === 'seed' || item.kind === 'gold') {
                const base = item.kind === 'seed' ? C.SCORE.seed : C.SCORE.gold;
                const score = base * (multActive ? C.MULTIPLIER.value : 1);
                state.score += score;
                events.push({ type: 'catch', kind: item.kind, x, score });
                if (state.score >= C.THRESHOLD) {
                    // Поріг і Є фінал: набрав 1000 — гра закінчилась. Виходимо
                    // з циклу, щоб інші предмети цього ж кадру не встигли
                    // відібрати життя і перетворити перемогу на «Ой, жук!».
                    state.thresholdTakenAt = state.time;
                    state.finished = true;
                    state.finishReason = 'threshold';
                    events.push({ type: 'threshold' });
                    events.push({ type: 'finished', reason: 'threshold' });
                    break;
                }
            } else if (item.kind === 'mult') {
                state.multUntil = state.time + C.MULTIPLIER.duration;
                events.push({ type: 'catch', kind: 'mult', x, score: 0 });
                events.push({ type: 'multiplierStart' });
            } else if (state.time >= state.iframesUntil) {
                state.lives -= 1;
                state.iframesUntil = state.time + C.IFRAMES;
                events.push({ type: 'hit', x, livesLeft: state.lives });
            }
        }
        state.items.length = write;

        // Поріг уже міг закрити партію — не перезаписуємо причину й не шлемо
        // другу подію finished.
        if (state.finished) return events;

        if (state.lives <= 0) {
            state.finished = true;
            state.finishReason = 'lives';
            events.push({ type: 'finished', reason: 'lives' });
        } else if (state.time >= C.DURATION) {
            state.finished = true;
            state.finishReason = 'time';
            events.push({ type: 'finished', reason: 'time' });
        }
        return events;
    };

    return { state, tick };
};

/** Bug sine path: ±24px on a 390px field ≈ ±0.06 normalized, 0.5 s period. */
export const bugX = (item: FallingItem, time: number): number => {
    const raw = item.x + 0.06 * Math.sin(((time - item.born) / 0.5) * Math.PI * 2);
    return Math.min(Math.max(raw, 0.02), 0.98);
};
